/**
 * Live "My Orders" page (carrier-dashboard, route: `/my-orders`).
 *
 * Thin host: mounts the shared `OrderTableManager`. Entity drawers
 * mount once in `DashboardLayout` via `AgentEntityDrawers`. The CRM
 * client workspace's Orders tab uses the same manager scoped by
 * `clientId`, so any UX/data-fetching change to the orders surface
 * lands in one place.
 *
 * NOTE for developers and AI agents: there is also a mock-only
 * design-review surface at `/my-orders/preview` showing a
 * financing-style expandable layout we may promote in the future.
 * It lives in `./preview/` and MUST NOT be promoted, imported, or
 * wired to live data without an explicit go-ahead from the product
 * owner. See `./preview/README.md` and the frontend Order docs at
 * `libs/frontend/web/components/src/feature/Order/README.md` for
 * the full rules and promotion path.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderTableManager } from '@hero/web-components';
import { usePageContext } from '@hero/web-context';
import { useUpdateOrder } from '@/components/hooks/Order/useOrderAPIs';
import { useOrderAgainConfig } from '@hero/web-hooks';
import type { OrderUpdateAPI } from '@hero/api';
import { getObjectId } from '@hero/schema';

export default function MyOrdersPage() {
  const { t } = useTranslation(['dashboard']);
  const { setPageName } = usePageContext();

  useEffect(() => {
    setPageName(t('dashboard:MyOrders.title'));
  }, [t, setPageName]);

  const { mutateAsync: updateOrderMutation } = useUpdateOrder();
  // Carrier-side reorder config — the unified carrier dashboard hosts.
  // the same "Order Again" affordance customer-dashboard does (see).
  // `useOrderDisplayInfo.visibility.showOrderAgainButton`). The hook.
  // wires the carrier SDK into the shared `useOrderAgain` flow.
  const orderAgainConfig = useOrderAgainConfig();

  const handleUpdateOrder = async (data: { _id: string; pod: { podRef?: string } }) => {
    const updateData: OrderUpdateAPI['req'] = {
      _id: getObjectId(data._id),
      pod: { podRef: data.pod.podRef },
    };
    return await updateOrderMutation(updateData);
  };

  return (
    <div className="mt-6">
      <OrderTableManager
        displayForUser="carrier"
        useGlobalFilterStore
        updateOrderFn={handleUpdateOrder}
        drawerProps={{ orderAgainConfig }}
        columns={[
          { key: 'ref' },
          { key: 'destinations' },
          { key: 'status' },
          { key: 'driver' },
          { key: 'price' },
        ]}
      />
    </div>
  );
}
